import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Settings, AlertCircle } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  title?: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  controls?: boolean;
  showFullscreen?: boolean;
  showQualitySelector?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  showTitle?: boolean;
  key?: string | number; // Force remounting when key changes
}

type VideoQuality = '480p' | '720p' | '1080p' | '1440p' | '4K';

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  title,
  className = '',
  autoPlay = false,
  loop = false,
  controls = true,
  showFullscreen = true,
  showQualitySelector = true,
  onPlay,
  onPause,
  showTitle = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [userInitiatedLoad, setUserInitiatedLoad] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<VideoQuality>('1080p');
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const controlsTimeout = useRef<NodeJS.Timeout | null>(null);
  
  const qualities: VideoQuality[] = ['480p', '720p', '1080p', '1440p', '4K'];

  // Reset all state when src changes
  const resetPlayerState = useCallback(() => {
    setIsPlaying(false);
    setIsLoading(false);
    setHasError(false);
    setErrorMessage('');
    setProgress(0);
    setDuration(0);
    setCurrentTime(0);
    setShowControls(false);
    setShowQualityMenu(false);
    setVideoLoaded(false);
    
    // Clear any existing timeouts
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
      controlsTimeout.current = null;
    }
  }, []);

  // Reset state when src changes
  useEffect(() => {
    resetPlayerState();
  }, [src, resetPlayerState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
    };
  }, []);
  
  // Handle video events
  useEffect(() => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    
    const handleLoadStart = () => {
      if (userInitiatedLoad) {
        setIsLoading(true);
      }
      setHasError(false);
      setVideoLoaded(false);
    };
    
    const handleCanPlay = () => {
      setIsLoading(false);
      setVideoLoaded(true);
      setHasError(false);
    };
    
    const handleError = (e: Event) => {
      console.error('Video error:', e);
      setIsLoading(false);
      setHasError(true);
      setErrorMessage('Video konnte nicht geladen werden');
      setIsPlaying(false);
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
      setHasError(false);
      if (onPlay) onPlay();
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      if (onPause) onPause();
    };
    
    const handleTimeUpdate = () => {
      if (video.duration && !isNaN(video.duration) && video.duration > 0) {
        setCurrentTime(video.currentTime);
        setProgress((video.currentTime / video.duration) * 100);
      }
    };
    
    const handleDurationChange = () => {
      if (!isNaN(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(100);
      if (onPause) onPause();
    };

    const handleWaiting = () => {
      if (userInitiatedLoad) {
        setIsLoading(true);
      }
    };

    const handlePlaying = () => {
      setIsLoading(false);
    };
    
    // Add all event listeners
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    
    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [onPlay, onPause]);
  
  // Auto-hide controls
  useEffect(() => {
    if (!controls || !isPlaying) return;
    
    const hideControls = () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
      
      controlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };
    
    if (showControls) {
      hideControls();
    }
    
    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
    };
  }, [showControls, isPlaying, controls]);
  
  const togglePlayPause = async () => {
    if (!videoRef.current || hasError) return;
    
    try {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        setUserInitiatedLoad(true);
        // Load video only when user wants to play
        if (!videoLoaded) {
          setIsLoading(true);
          videoRef.current.load();
        }
        
        // Unmute when playing
        if (isMuted) {
          videoRef.current.muted = false;
          setIsMuted(false);
        }
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('Error toggling video playback:', error);
      setHasError(true);
      setErrorMessage('Fehler beim Abspielen des Videos');
    }
  };
  
  const toggleMute = () => {
    if (!videoRef.current || hasError) return;
    
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };
  
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current || !videoRef.current.duration || isNaN(videoRef.current.duration) || hasError) return;
    
    const newTime = (parseFloat(e.target.value) / 100) * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(parseFloat(e.target.value));
  };
  
  const toggleFullscreen = () => {
    if (!playerRef.current || hasError) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      playerRef.current.requestFullscreen();
    }
  };
  
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  const handleQualityChange = (quality: VideoQuality) => {
    setSelectedQuality(quality);
    setShowQualityMenu(false);
    
    // In a real implementation, you would switch the video source based on quality
    console.log(`Quality changed to ${quality}`);
  };

  const retryVideo = () => {
    if (!videoRef.current) return;
    
    resetPlayerState();
    videoRef.current.load();
  };
  
  return (
    <div 
      ref={playerRef}
      className={`relative group bg-black ${className}`}
      onMouseEnter={() => !hasError && setShowControls(true)}
      onMouseLeave={() => !hasError && isPlaying && setShowControls(false)}
      onClick={() => !hasError && setShowControls(true)}
    >
      {/* Error State */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
          <div className="text-center p-8">
            <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
            <h3 className="text-xl font-semibold mb-2">Video nicht verfügbar</h3>
            <p className="text-gray-400 mb-4">{errorMessage}</p>
            <button
              onClick={retryVideo}
              className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && userInitiatedLoad && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-sm text-gray-300">Video wird geladen...</p>
          </div>
        </div>
      )}
      
      {/* Poster Image - Always visible until video starts playing */}
      {poster && !isPlaying && !hasError && (
        <div className="absolute inset-0 z-5">
          <img
            src={poster}
            alt={title || 'Video Thumbnail'}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        src={src}
        poster={poster}
        muted={isMuted}
        loop={loop}
        playsInline
        preload="none" // Only load when needed
        controlsList="nodownload"
        disablePictureInPicture
        style={{ display: hasError ? 'none' : 'block' }}
      />
      
      {/* Play button overlay (when paused and no error) */}
      {!isPlaying && !hasError && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
          onClick={togglePlayPause}
        >
          <div className="p-4 rounded-full bg-black/50 hover:bg-black/70 transition-all duration-200 hover:scale-110">
            <Play size={32} className="ml-1" />
          </div>
        </div>
      )}
      
      {/* Video controls */}
      {controls && !hasError && (
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 transition-opacity duration-300 ${
            showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {showTitle && title && (
            <div className="mb-2">
              <h3 className="text-lg font-semibold">{title}</h3>
            </div>
          )}
          
          {/* Progress bar */}
          <div className="flex items-center mb-1">
            <span className="text-xs mr-2">{formatTime(currentTime)}</span>
            <div className="flex-1 relative h-1 bg-gray-600 rounded-full">
              <input
                type="range"
                min="0"
                max="100"
                value={isNaN(progress) ? 0 : progress}
                onChange={handleProgressChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={!videoLoaded}
              />
              <div 
                className="absolute top-0 left-0 h-full bg-white rounded-full transition-all duration-100"
                style={{ width: `${isNaN(progress) ? 0 : progress}%` }}
              ></div>
            </div>
            <span className="text-xs ml-2">{formatTime(duration)}</span>
          </div>
          
          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={togglePlayPause}
                className="p-1 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                aria-label={isPlaying ? 'Pause' : 'Play'}
                disabled={!videoLoaded && !isPlaying}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              
              <button
                onClick={toggleMute}
                className="p-1 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                disabled={!videoLoaded}
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              {showQualitySelector && (
                <div className="relative">
                  <button
                    onClick={() => setShowQualityMenu(!showQualityMenu)}
                    className="p-1 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                    aria-label="Quality settings"
                    disabled={!videoLoaded}
                  >
                    <Settings size={16} />
                  </button>
                  
                  {showQualityMenu && (
                    <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded-md p-2 min-w-32">
                      <div className="text-xs font-medium mb-1 px-2">Qualität</div>
                      {qualities.map((quality) => (
                        <button
                          key={quality}
                          className={`block w-full text-left px-2 py-1 text-xs rounded hover:bg-white/20 ${
                            selectedQuality === quality ? 'bg-white/20' : ''
                          }`}
                          onClick={() => handleQualityChange(quality)}
                        >
                          {quality}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {showFullscreen && (
                <button
                  onClick={toggleFullscreen}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                  aria-label="Vollbild"
                  disabled={!videoLoaded}
                >
                  <Maximize size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;